import type { AppItemBreakdown } from "@/features/app-studio/api/appBreakdownsApi";
import type { AppQueryBuilderCompileRequest } from "@/features/app-studio/api/appQueryBuilderApi";
import type { AppWidgetInputMode } from "@/features/app-studio/lib/appChartLayout";
import type { AppComparisonPeriods } from "@/features/app-studio/lib/comparisonDisplay";
import type { ChartType, GeneralizedChartDefinition } from "@/features/chat/model/chatTypes";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppChartSummary = {
  id: number;
  app_id: number;
  /** The chart's SQL lives on its referenced AppQuery; not returned on the summary. */
  sql?: string | null;
  /** Reference to the reusable AppQuery that supplies this chart's SQL. */
  query_id?: number | null;
  chart_definition: GeneralizedChartDefinition;
  /** User-selected chart type that overrides `recommended_chart_type`; null when unset. */
  preferred_chart_type?: ChartType | null;
  column_span: number;
  sort_order: number;
  /** False when the author hid this chart's AI summary; absent on rows from older servers. */
  show_summary?: boolean;
  tab_id?: number | null;
  input_mode: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  created_at: string;
  updated_at: string;
};

export type AppChartDataItem = {
  id: number;
  /** See `AppTableDataItem.query_id` — the saved query this widget renders, when it has one. */
  query_id?: number | null;
  chart_definition: GeneralizedChartDefinition;
  /** User-selected chart type that overrides `recommended_chart_type`; null when unset. */
  preferred_chart_type?: ChartType | null;
  column_span?: number;
  sort_order: number;
  tab_id?: number | null;
  data: Record<string, unknown>[];
  error?: string | null;
  /** Display names of active tab filters that did not apply to this widget (drives the badge). */
  ignored_filters?: string[] | null;
  /**
   * True when this widget is some KPI card's drill-down widget: it is excluded from the tab
   * grid and only rendered inside the drill-down dialog.
   */
  is_drilldown_target?: boolean;
  /**
   * False when the author hid this chart's AI summary. Display-only: the summary is still
   * generated, because the tab summary is built from it. Absent means shown.
   */
  show_summary?: boolean;
  /**
   * The stored AI summary. `undefined` on the published stream's `item` frame, where it arrives in a
   * later `item_summary` frame; `null` means either not generated yet, or generated and found
   * unsummarisable (an errored widget or one with no rows).
   */
  summary?: string | null;
  /**
   * The breakdown this chart is linked to and the option its rows were run for; `null` or absent
   * when it has none. Read it through `itemBreakdownOf`, which tolerates a malformed stamp.
   */
  breakdown?: AppItemBreakdown | null;
  /**
   * The two periods the comparison query compared on this run; null or absent for any other query.
   * Read it through `comparisonPeriodsOf`.
   */
  comparison_periods?: AppComparisonPeriods | null;
};

export type AppChartsDataResponse = {
  charts: AppChartDataItem[];
};

function isGeneralizedChartDefinition(value: unknown): value is GeneralizedChartDefinition {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.id === "string" && typeof row.recommended_chart_type === "string";
}

function isAppChartSummary(value: unknown): value is AppChartSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    (row.sql === null || row.sql === undefined || typeof row.sql === "string") &&
    (row.query_id === undefined || row.query_id === null || typeof row.query_id === "number") &&
    isGeneralizedChartDefinition(row.chart_definition) &&
    (row.preferred_chart_type === undefined ||
      row.preferred_chart_type === null ||
      typeof row.preferred_chart_type === "string") &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    (row.show_summary === undefined || typeof row.show_summary === "boolean") &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    (row.input_mode === undefined || typeof row.input_mode === "string") &&
    (row.input_config === undefined ||
      row.input_config === null ||
      typeof row.input_config === "object") &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppChartDataItem(value: unknown): value is AppChartDataItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    isGeneralizedChartDefinition(row.chart_definition) &&
    (row.preferred_chart_type === undefined ||
      row.preferred_chart_type === null ||
      typeof row.preferred_chart_type === "string") &&
    (row.column_span === undefined || typeof row.column_span === "number") &&
    (row.show_summary === undefined || typeof row.show_summary === "boolean") &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    Array.isArray(row.data) &&
    (row.error === undefined || row.error === null || typeof row.error === "string")
  );
}

export async function fetchAppCharts(appId: number): Promise<AppChartSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/charts`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppChartSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function fetchAppChartsData(appId: number): Promise<AppChartDataItem[]> {
  const res = await apiFetch(`/v1/apps/${appId}/charts/data`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  const charts = (data as AppChartsDataResponse).charts;
  if (!Array.isArray(charts) || !charts.every(isAppChartDataItem)) {
    throw new Error("Unexpected response shape");
  }
  return charts;
}

export type AddChartToAppPayload = {
  /** Provide exactly one of `sql` (inline) or `query_id` (reuse a saved query). */
  sql?: string;
  query_id?: number | null;
  chart_definition?: GeneralizedChartDefinition;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
};

export type UpdateAppChartPayload = {
  sql?: string;
  query_id?: number | null;
  chart_definition?: GeneralizedChartDefinition;
  preferred_chart_type?: ChartType | null;
  column_span?: number;
  show_summary?: boolean;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
};

export async function addChartToApp(
  appId: number,
  payload: AddChartToAppPayload,
): Promise<AppChartSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/charts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppChartSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppChart(
  appId: number,
  chartId: number,
  payload: UpdateAppChartPayload,
): Promise<AppChartSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/charts/${chartId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppChartSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppChart(appId: number, chartId: number): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/charts/${chartId}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
